import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AboTileData,
  RequirementType,
  AboBronzeEligibility,
  BGTOverviewKPIs,
} from "../../services/BronzeGroupTracking/bronzeGroupTracking.types";
import { BRONZE_TAGS } from "../model/bgtFilters";
import { isProgramActiveBulkCheck } from "../../common/util/checkProgramActiveStatus";
import { setActiveTabView } from "../bronzeGroupTrackingTab";
import { setShellConfig } from "../boot";
import { EligibleIncentive } from "../../services/MyBizShell/mybizShellAPI.types";
import { TrendEnum } from "../../common/leaf/LeafTrendIndicator/LeafTrendIndicator";
import { BGT_TABS } from "../../common/enums/bronze-group-tracking-enums";

interface ABOProfileData {
  affNumber: string;
  aboNumber: string;
  aboName: string;
  aboLocalName: string;
  displayName: string;
  isoLanguage: string;
  graduationPeriod: string;
  bronzeIncentive: string;
  bronzeFoundation: AboTileData[];
  bronzeBuilder: AboTileData[];
}

const initialProfileData = {
  affNumber: "0",
  aboNumber: "0",
  aboName: "",
  aboLocalName: "",
  displayName: "",
  isoLanguage: "",
  graduationPeriod: "",
  bronzeIncentive: "",
  bronzeFoundation: [] as AboTileData[],
  bronzeBuilder: [] as AboTileData[],
};

interface BronzeGroupTrackingProfileState {
  showProfile: boolean;
  currentProfileAboData: ABOProfileData;
}

interface BronzeGroupTrackingState {
  initialTrackingTilesData: AboTileData[];
  initialGraduatedTilesData: AboTileData[];
  finalTrackingTilesData: AboTileData[];
  finalGraduatedTilesData: AboTileData[];
  requirementFilters: RequirementType[];
  restartIsActive?: boolean;
  activeView?: string;
}

interface BGTData extends BronzeGroupTrackingState, BronzeGroupTrackingProfileState {
  aboBronzeEligibility?: AboBronzeEligibility[];
  isLoading: boolean;
}
interface BGTData extends BronzeGroupTrackingState, BronzeGroupTrackingProfileState {
  overviewKpis: BGTOverviewKPIs;
}

interface FinalTrackingState {
  finalTrackingTilesData: AboTileData[];
}

interface FinalGraduatedState {
  finalGraduatedTilesData: AboTileData[];
}

export const initialStateBTG: BGTData = {
  initialTrackingTilesData: [],
  initialGraduatedTilesData: [],
  finalTrackingTilesData: [],
  finalGraduatedTilesData: [],
  requirementFilters: [],
  restartIsActive: true,
  showProfile: false,
  currentProfileAboData: initialProfileData,
  activeView: BGT_TABS.OVERVIEW_TAB,
  aboBronzeEligibility: [],
  isLoading: false,
  overviewKpis: {
    selectedPeriod: {
      closeCount: 0,
      newCount: 0,
      inWindowCount: 0,
      graduatedCount: 0,
    },
    previousPeriod: {
      closeCount: 0,
      newCount: 0,
      inWindowCount: 0,
      graduatedCount: 0,
    },
    kpiTrends: {
      closeCount: {
        Difference: 0,
        Trend: TrendEnum.EQUAL,
      },
      newCount: {
        Difference: 0,
        Trend: TrendEnum.EQUAL,
      },
      inWindowCount: {
        Difference: 0,
        Trend: TrendEnum.EQUAL,
      },
      graduatedCount: {
        Difference: 0,
        Trend: TrendEnum.EQUAL,
      },
    },
  },
};

const findBronzeDataForCurrentAbo = (aff: string, abo: string, data: AboTileData[]) => {
  const currentAbo = data.filter((item) => item.affNumber === aff && item.aboNumber === abo);
  const bronzeFoundation = currentAbo.filter((data) => data.incentiveTag === BRONZE_TAGS.BRONZE_FOUNDATION);
  const bronzeBuilder = currentAbo.filter((data) => data.incentiveTag === BRONZE_TAGS.BRONZE_BUILDER);
  return {
    bronzeFoundation,
    bronzeBuilder,
  };
};

const { actions, reducer } = createSlice({
  name: "bronzeGroupTracking",
  initialState: initialStateBTG,
  reducers: {
    /**
     * Once data is recieved from API it is set here and does not update unless the api is called again.
     */
    updateBronzeGroupList: (state, { payload }: PayloadAction<BronzeGroupTrackingState>) => {
      return {
        ...state,
        ...payload,
      };
    },

    updateOverviewKPIs: (state, { payload }: PayloadAction<BGTOverviewKPIs>) => {
      return {
        ...state,
        overviewKpis: payload,
      };
    },
    /**
     * Independently updates the finalTrackingTilesData as it is getting filtered, sorted, and searched and then saves the state of updated data.
     */
    updateFinalTrackingData: (state, { payload }: PayloadAction<FinalTrackingState>) => {
      return {
        ...state,
        ...payload,
      };
    },
    /**
     * Independently updates the finalGraduatedTilesData as it is getting filtered, sorted, and searched and then saves the state of updated data.
     */
    updateFinalGraduatedData: (state, { payload }: PayloadAction<FinalGraduatedState>) => {
      return {
        ...state,
        ...payload,
      };
    },
    updateBronzeEligibilityData: (state, { payload }: PayloadAction<AboBronzeEligibility[]>) => {
      return {
        ...state,
        aboBronzeEligibility: payload,
      };
    },
    openProfileModal: (state, { payload }: PayloadAction<AboTileData>) => {
      const {
        aboNumber,
        affNumber,
        aboName,
        aboLocalName,
        displayName,
        isoLanguage,
        incentiveTag: bronzeIncentive,
        graduationPeriod,
        graduated,
      } = payload;
      const currentTabData =
        state.activeView === BGT_TABS.GRADUATED_TAB ? state.initialGraduatedTilesData : state.initialTrackingTilesData;
      const { bronzeFoundation, bronzeBuilder } = findBronzeDataForCurrentAbo(affNumber, aboNumber, currentTabData);

      return {
        ...state,
        showProfile: true,
        currentProfileAboData: {
          aboName,
          aboLocalName,
          displayName,
          aboNumber,
          affNumber,
          isoLanguage,
          graduationPeriod: graduated ? graduationPeriod : "",
          bronzeIncentive,
          bronzeFoundation,
          bronzeBuilder,
        },
      };
    },
    closeProfileModal: (state) => {
      return {
        ...state,
        showProfile: false,
        currentProfileAboData: initialProfileData,
      };
    },
    updateLoadingFlag: (state, payload) => {
      return {
        ...state,
        isLoading: payload.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setShellConfig, (state, action) => {
      const { eligibleIncentives } = action.payload;

      const getRestartIsActive = (eligibleIncentives: EligibleIncentive[]) => {
        let restartIsActive = false;
        if (eligibleIncentives) {
          restartIsActive = isProgramActiveBulkCheck(eligibleIncentives, [948, 949]);
        }

        return restartIsActive;
      };

      state.restartIsActive = getRestartIsActive(eligibleIncentives);
    });
    builder.addCase(setActiveTabView, (state, action) => {
      state.activeView = action.payload;
    });
  },
});

export const {
  updateBronzeGroupList,
  updateFinalTrackingData,
  updateFinalGraduatedData,
  updateBronzeEligibilityData,
  openProfileModal,
  closeProfileModal,
  updateOverviewKPIs,
  updateLoadingFlag,
} = actions;

export default reducer;
